<script setup>
import { WdsButtonGhost, WdsEventCard } from '@wds/components';

const props = defineProps({
  eventList: {
    type: Array,
    required: true,
  },
});

// ### COMPOSABLES
const {
  t,
  locale,
} = useI18n();
const { useUrl } = useUrls();
const { useFormatter } = useFormatters();

// ### COMPUTED
const hasValidEvents = computed(() => {
  return Array.isArray(props.eventList) && props.eventList?.length > 0;
});

const eventListForCards = computed(() => {
  return props.eventList?.map((event) => {
    const eventPicture = event?.picture?.mobile || '';

    const eventLocation = event?.locationName || '';

    const eventLink = useUrl.getInternalEventPageUrl({
      rootSlug: t('t_OGEVENTSSLUG'),
      eventId: event.eventId,
      eventNameSlug: useFormatter.normalizeUrl(event.name),
    });

    return {
      id: event.eventId,
      bind: {
        language: locale.value,
        name: event.name,
        href: eventLink,
        startDate: event.dates?.startDate,
        endDate: event.dates?.endDate,
        category: event.eventType,
        picture: eventPicture,
        location: eventLocation,
      },
    };
  });
});

const eventCityName = computed(() => {
  const firstLocation = props.eventList?.[0]?.location;
  return firstLocation?.city?.name || firstLocation?.cityName || '';
});

const cityEventsPageUrl = computed(() => {
  const eventLocation = props.eventList?.[0]?.location;

  if (!eventLocation) {
    return '';
  }

  return useUrl.getCityPageUrl({
    propertyTypeSlug: t('t_OGEVENTSSLUG'),
    urlFriendlyContinent: eventLocation?.continent?.urlFriendlyName,
    urlFriendlyCountry: eventLocation?.country?.urlFriendlyName,
    urlFriendlyCity: eventLocation?.city?.urlFriendlyName,
  });
});
</script>

<template>
  <div
    v-if="hasValidEvents"
    class="more-events-in-city-container"
  >
    <h2 class="more-events-in-city-title">
      <i18n-t keypath="t_OGEVENTMOREEVENTSINCITY">
        <template #CITYNAME>
          <span>{{ eventCityName }}</span>
        </template>
      </i18n-t>
    </h2>
    <CommonHwCarousel :item-count="eventList.length">
      <WdsEventCard
        v-for="event in eventListForCards"
        :key="event.id"
        class="more-events-in-city-card"
        orientation="horizontal"
        v-bind="event.bind"
      />
    </CommonHwCarousel>
    <WdsButtonGhost
      class="more-events-in-city-show-more"
      :text="$t('t_VIEWMORE')"
      icon-end="arrow-right"
      :href="cityEventsPageUrl"
    />
  </div>
</template>

<style lang="scss" scoped>
.more-events-in-city-container {
  display: flex;
  flex-direction: column;
  gap: $wds-spacing-m;

  .more-events-in-city-title {
    @include title-3-bld;

    overflow-wrap: break-word;

    :deep(span) {
      color: $wds-color-purple;
    }
  }

  .more-events-in-city-card {
    margin-bottom: $wds-spacing-m;
    width: auto;
  }

  .more-events-in-city-show-more {
    margin-left: auto;
  }

  @include tablet-large {
    .more-events-in-city-title {
      @include title-2-bld;
    }
  }
}
</style>
